import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Coins, Settings, ArrowUpDown, Wallet, Users2, TrendingUp,
  Save, Award, ChevronDown, ChevronUp, Search, RefreshCw,
  Loader2, ArrowLeft, ArrowRight, CircleDollarSign, Zap,
  Gift, Plus, Trash2, Edit2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SIGNAL_CATEGORIES = ['Attendance', 'Planning', 'Tasks', 'Finance', 'Collaboration', 'Engagement', 'Milestones', 'Recognition'];
const CATEGORY_COLORS = {
  Attendance: 'bg-blue-100 text-blue-700',
  Planning: 'bg-purple-100 text-purple-700',
  Tasks: 'bg-green-100 text-green-700',
  Finance: 'bg-amber-100 text-amber-700',
  Collaboration: 'bg-cyan-100 text-cyan-700',
  Engagement: 'bg-pink-100 text-pink-700',
  Milestones: 'bg-orange-100 text-orange-700',
  Recognition: 'bg-indigo-100 text-indigo-700',
};

const EcoCoins = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('config');
  const [loading, setLoading] = useState(true);

  // Config state
  const [signals, setSignals] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [showCurrencyBalance, setShowCurrencyBalance] = useState(true);
  const [savingSignals, setSavingSignals] = useState(false);
  const [savingConversions, setSavingConversions] = useState(false);

  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [txnTotal, setTxnTotal] = useState(0);
  const [txnPage, setTxnPage] = useState(1);
  const [txnTotalPages, setTxnTotalPages] = useState(1);
  const [txnFilter, setTxnFilter] = useState('all');
  const [txnOffice, setTxnOffice] = useState('all');
  const [txnSearch, setTxnSearch] = useState('');
  const [txnLoading, setTxnLoading] = useState(false);

  // Balances state
  const [balancesData, setBalancesData] = useState({ offices: [], total_coins_in_circulation: 0, total_employees: 0 });
  const [balOffice, setBalOffice] = useState('all');
  const [expandedOffice, setExpandedOffice] = useState(null);
  const [balLoading, setBalLoading] = useState(false);

  // Summary state
  const [summary, setSummary] = useState(null);

  // Award modal
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [awardData, setAwardData] = useState({ user_id: '', coins: '', reason: '' });
  const [awardProcessing, setAwardProcessing] = useState(false);
  const [employees, setEmployees] = useState([]);

  // Offices
  const [offices, setOffices] = useState([]);

  // Rewards state
  const [rewards, setRewards] = useState([]);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [savingRewards, setSavingRewards] = useState(false);

  const headers = getAuthHeaders();

  const fetchConfig = useCallback(async () => {
    try {
      const [configRes, convRes] = await Promise.all([
        fetch(`${API_URL}/api/ecocoins/config`, { headers }),
        fetch(`${API_URL}/api/ecocoins/config/conversions`, { headers }),
      ]);
      if (configRes.ok) {
        const data = await configRes.json();
        setSignals(data.signals || []);
        setShowCurrencyBalance(data.show_currency_balance !== false);
      }
      if (convRes.ok) {
        const data = await convRes.json();
        setConversions(data.conversions || []);
      }
    } catch { toast.error('Failed to load config'); }
  }, []);

  const fetchTransactions = useCallback(async (page = 1) => {
    setTxnLoading(true);
    try {
      let url = `${API_URL}/api/ecocoins/transactions?page=${page}&page_size=30`;
      if (txnFilter !== 'all') url += `&txn_type=${txnFilter}`;
      if (txnOffice !== 'all') url += `&office_id=${txnOffice}`;
      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        setTransactions(data.transactions || []);
        setTxnTotal(data.total || 0);
        setTxnPage(data.page || 1);
        setTxnTotalPages(data.total_pages || 1);
      }
    } catch { toast.error('Failed to load transactions'); }
    finally { setTxnLoading(false); }
  }, [txnFilter, txnOffice]);

  const fetchBalances = useCallback(async () => {
    setBalLoading(true);
    try {
      let url = `${API_URL}/api/ecocoins/balances`;
      if (balOffice !== 'all') url += `?office_id=${balOffice}`;
      const res = await fetch(url, { headers });
      if (res.ok) setBalancesData(await res.json());
    } catch { toast.error('Failed to load balances'); }
    finally { setBalLoading(false); }
  }, [balOffice]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ecocoins/summary`, { headers });
      if (res.ok) setSummary(await res.json());
    } catch {}
  }, []);

  const fetchOffices = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/offices`, { headers });
      if (res.ok) {
        const data = await res.json();
        setOffices(data.offices || data || []);
      }
    } catch {}
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/employees?limit=500`, { headers });
      if (res.ok) {
        const data = await res.json();
        setEmployees(data.employees || data || []);
      }
    } catch {}
  }, []);

  const fetchRewards = useCallback(async () => {
    setRewardsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ecocoins/marketplace?include_disabled=true`, { headers });
      if (res.ok) {
        const data = await res.json();
        setRewards(data.rewards || []);
      }
    } catch { toast.error('Failed to load rewards'); }
    finally { setRewardsLoading(false); }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchConfig(), fetchSummary(), fetchOffices()]);
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    if (activeTab === 'transactions') fetchTransactions(1);
  }, [activeTab, txnFilter, txnOffice]);

  useEffect(() => {
    if (activeTab === 'balances') fetchBalances();
  }, [activeTab, balOffice]);

  useEffect(() => {
    if (activeTab === 'rewards') fetchRewards();
  }, [activeTab]);

  const handleSaveSignals = async () => {
    setSavingSignals(true);
    try {
      const res = await fetch(`${API_URL}/api/ecocoins/config/signals`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(signals),
      });
      if (res.ok) toast.success('Signal configuration saved');
      else toast.error('Failed to save');
    } catch { toast.error('Network error'); }
    finally { setSavingSignals(false); }
  };

  const handleSaveConversions = async () => {
    setSavingConversions(true);
    try {
      const payload = conversions.map(c => ({
        office_id: c.office_id,
        currency: c.currency || 'TZS',
        coins_per_unit: parseFloat(c.coins_per_unit) || 1,
      }));
      const res = await fetch(`${API_URL}/api/ecocoins/config/conversions`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) toast.success('Conversion rates saved');
      else toast.error('Failed to save');
    } catch { toast.error('Network error'); }
    finally { setSavingConversions(false); }
  };

  const handleSaveRewards = async () => {
    setSavingRewards(true);
    try {
      const res = await fetch(`${API_URL}/api/ecocoins/marketplace/rewards`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(rewards),
      });
      if (res.ok) { toast.success('Rewards saved'); fetchRewards(); }
      else toast.error('Failed to save rewards');
    } catch { toast.error('Network error'); }
    finally { setSavingRewards(false); }
  };

  const addReward = () => {
    setRewards(prev => [...prev, {
      id: '', name: '', description: '', coins_cost: 100,
      category: 'General', enabled: true, stock: -1,
    }]);
  };

  const updateReward = (idx, field, value) => {
    setRewards(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const removeReward = (idx) => {
    setRewards(prev => prev.filter((_, i) => i !== idx));
  };

  const handleAward = async () => {
    if (!awardData.user_id || !awardData.coins || !awardData.reason) {
      toast.error('Fill in all fields'); return;
    }
    setAwardProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/ecocoins/award`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...awardData, coins: parseInt(awardData.coins) }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        setShowAwardModal(false);
        setAwardData({ user_id: '', coins: '', reason: '' });
        if (activeTab === 'transactions') fetchTransactions(txnPage);
        if (activeTab === 'balances') fetchBalances();
        fetchSummary();
      } else toast.error(data.detail || 'Failed');
    } catch { toast.error('Network error'); }
    finally { setAwardProcessing(false); }
  };

  const tabs = [
    { id: 'config', label: 'Configuration', icon: Settings },
    { id: 'rewards', label: 'Rewards', icon: Gift },
    { id: 'transactions', label: 'Transactions', icon: ArrowUpDown },
    { id: 'balances', label: 'Balances', icon: Wallet },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-[#0779BC]" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Coins className="h-7 w-7 text-amber-500" />
            EcoCoins
          </h1>
          <p className="text-sm text-gray-500 mt-1">Configure and manage the virtual employee currency</p>
        </div>
        <Button data-testid="award-coins-btn" className="bg-amber-500 hover:bg-amber-600" onClick={() => { setShowAwardModal(true); fetchEmployees(); }}>
          <Award className="h-4 w-4 mr-2" /> Award Coins
        </Button>
      </div>

      {/* Summary KPIs */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-amber-400">
            <CardContent className="p-4">
              <p className="text-[10px] uppercase text-gray-500 tracking-wide">Total in Circulation</p>
              <p className="text-2xl font-bold text-gray-900">{(summary.total_coins_in_circulation || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-400">{summary.total_holders || 0} holders</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-400">
            <CardContent className="p-4">
              <p className="text-[10px] uppercase text-gray-500 tracking-wide">Earned This Month</p>
              <p className="text-2xl font-bold text-green-700">{(summary.monthly_earned || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-400">{summary.monthly_earned_count || 0} transactions</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-400">
            <CardContent className="p-4">
              <p className="text-[10px] uppercase text-gray-500 tracking-wide">Redeemed This Month</p>
              <p className="text-2xl font-bold text-red-600">{(summary.monthly_redeemed || 0).toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-400">
            <CardContent className="p-4">
              <p className="text-[10px] uppercase text-gray-500 tracking-wide">Top Signal</p>
              <p className="text-lg font-bold text-gray-900">
                {summary.signal_breakdown?.[0]?.signal?.replace(/_/g, ' ') || 'None'}
              </p>
              <p className="text-xs text-gray-400">{summary.signal_breakdown?.[0]?.coins?.toLocaleString() || 0} coins</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {tabs.map(tab => (
          <button key={tab.id} data-testid={`ecocoins-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon className="h-4 w-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Configuration Tab */}
      {activeTab === 'config' && (
        <div className="grid grid-cols-3 gap-6">
          {/* Signals config (2 cols) */}
          <div className="col-span-2 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Zap className="h-4 w-4 text-amber-500" /> Coin Generation Signals
                    </CardTitle>
                    <CardDescription>Configure how many coins each activity earns</CardDescription>
                  </div>
                  <Button data-testid="save-signals-btn" size="sm" className="bg-[#0779BC] hover:bg-[#065d94]" disabled={savingSignals} onClick={handleSaveSignals}>
                    {savingSignals ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                    Save
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {/* Group by category */}
                  {SIGNAL_CATEGORIES.filter(cat => signals.some(s => s.category === cat)).map(category => (
                    <div key={category}>
                      <p className="text-[10px] uppercase text-gray-400 tracking-wider font-semibold mb-1.5 mt-3">{category}</p>
                      {signals.filter(s => s.category === category).map((signal, idx) => {
                        const globalIdx = signals.findIndex(s => s.signal === signal.signal);
                        return (
                          <div key={signal.signal} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-gray-50 border border-transparent hover:border-gray-100">
                            <Switch
                              checked={signal.enabled}
                              onCheckedChange={(val) => {
                                const updated = [...signals];
                                updated[globalIdx] = { ...updated[globalIdx], enabled: val };
                                setSignals(updated);
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800">{signal.label}</p>
                              <p className="text-[10px] text-gray-400 font-mono">{signal.signal}</p>
                            </div>
                            <Badge className={`${CATEGORY_COLORS[signal.category] || 'bg-gray-100'} text-[10px]`}>{signal.category}</Badge>
                            <div className="flex items-center gap-1 w-24">
                              <Input
                                type="number"
                                className="h-8 text-center font-bold text-sm"
                                value={signal.coins}
                                onChange={(e) => {
                                  const updated = [...signals];
                                  updated[globalIdx] = { ...updated[globalIdx], coins: parseInt(e.target.value) || 0 };
                                  setSignals(updated);
                                }}
                              />
                              <span className="text-xs text-gray-400">coins</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Conversions config (1 col) */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <CircleDollarSign className="h-4 w-4 text-green-600" /> Currency Conversion
                    </CardTitle>
                    <CardDescription>Coins per currency unit per office</CardDescription>
                  </div>
                  <Button data-testid="save-conversions-btn" size="sm" className="bg-[#0779BC] hover:bg-[#065d94]" disabled={savingConversions} onClick={handleSaveConversions}>
                    {savingConversions ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                    Save
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {conversions.map((conv, idx) => (
                  <div key={conv.office_id} className="p-3 bg-gray-50 rounded-lg border space-y-2">
                    <p className="text-xs font-semibold text-gray-700">{conv.office_name || conv.office_id}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-6">1</span>
                      <Input
                        className="h-8 w-16 text-center text-xs font-mono"
                        value={conv.currency || 'TZS'}
                        onChange={(e) => {
                          const updated = [...conversions];
                          updated[idx] = { ...updated[idx], currency: e.target.value };
                          setConversions(updated);
                        }}
                      />
                      <span className="text-xs text-gray-500">=</span>
                      <Input
                        type="number"
                        className="h-8 w-20 text-center text-xs font-bold"
                        value={conv.coins_per_unit}
                        onChange={(e) => {
                          const updated = [...conversions];
                          updated[idx] = { ...updated[idx], coins_per_unit: parseFloat(e.target.value) || 0 };
                          setConversions(updated);
                        }}
                      />
                      <span className="text-xs text-amber-600 font-semibold">coins</span>
                    </div>
                  </div>
                ))}
                {conversions.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-4">No offices configured yet</p>
                )}
              </CardContent>
            </Card>

            {/* Display Settings */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Settings className="h-4 w-4 text-gray-500" /> Display Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                  <div>
                    <p className="text-sm font-medium text-gray-800">Show Currency Balance</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">When off, employees see ***** instead of currency amounts</p>
                  </div>
                  <Switch
                    data-testid="toggle-currency-balance"
                    checked={showCurrencyBalance}
                    onCheckedChange={async (val) => {
                      setShowCurrencyBalance(val);
                      try {
                        const res = await fetch(`${API_URL}/api/ecocoins/config/settings?show_currency_balance=${val}`, {
                          method: 'PUT', headers,
                        });
                        if (res.ok) toast.success(val ? 'Currency balance visible' : 'Currency balance hidden');
                        else toast.error('Failed to update');
                      } catch { toast.error('Network error'); }
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Rewards Tab */}
      {activeTab === 'rewards' && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Gift className="h-4 w-4 text-amber-500" /> Marketplace Rewards
                </CardTitle>
                <CardDescription>Configure rewards employees can redeem with their coins</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button data-testid="add-reward-btn" variant="outline" size="sm" onClick={addReward}>
                  <Plus className="h-4 w-4 mr-1" /> Add Reward
                </Button>
                <Button data-testid="save-rewards-btn" size="sm" className="bg-[#0779BC] hover:bg-[#065d94]" disabled={savingRewards} onClick={handleSaveRewards}>
                  {savingRewards ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                  Save All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {rewardsLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
            ) : rewards.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-400 mb-3">No rewards configured yet</p>
                <Button variant="outline" size="sm" onClick={addReward}>
                  <Plus className="h-4 w-4 mr-1" /> Add Your First Reward
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Header */}
                <div className="grid grid-cols-12 gap-3 px-3 text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
                  <div className="col-span-3">Name</div>
                  <div className="col-span-3">Description</div>
                  <div className="col-span-2">Category</div>
                  <div className="col-span-1 text-center">Coins</div>
                  <div className="col-span-1 text-center">Stock</div>
                  <div className="col-span-1 text-center">Active</div>
                  <div className="col-span-1"></div>
                </div>
                {rewards.map((rw, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-3 items-center p-3 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 transition-colors" data-testid={`reward-row-${idx}`}>
                    <div className="col-span-3">
                      <Input
                        data-testid={`reward-name-${idx}`}
                        className="h-8 text-sm"
                        placeholder="Reward name"
                        value={rw.name}
                        onChange={(e) => updateReward(idx, 'name', e.target.value)}
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        className="h-8 text-xs"
                        placeholder="Short description"
                        value={rw.description}
                        onChange={(e) => updateReward(idx, 'description', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Select value={rw.category} onValueChange={(v) => updateReward(idx, 'category', v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['Time Off', 'Food & Drink', 'Perks', 'Rewards', 'Swag', 'Recognition', 'Fun', 'General'].map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1">
                      <Input
                        data-testid={`reward-coins-${idx}`}
                        type="number"
                        className="h-8 text-center text-xs font-bold"
                        value={rw.coins_cost}
                        onChange={(e) => updateReward(idx, 'coins_cost', parseInt(e.target.value) || 0)}
                      />
                    </div>
                    <div className="col-span-1">
                      <Input
                        type="number"
                        className="h-8 text-center text-xs"
                        placeholder="-1"
                        title="-1 means unlimited"
                        value={rw.stock}
                        onChange={(e) => updateReward(idx, 'stock', parseInt(e.target.value))}
                      />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <Switch
                        checked={rw.enabled}
                        onCheckedChange={(v) => updateReward(idx, 'enabled', v)}
                      />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                        onClick={() => removeReward(idx)} data-testid={`reward-delete-${idx}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
                <p className="text-[10px] text-gray-400 mt-2 px-3">Stock: -1 = unlimited. Disabled rewards won't appear in the employee marketplace.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-base">Wallet Transactions</CardTitle>
              <div className="flex items-center gap-2">
                <Select value={txnFilter} onValueChange={setTxnFilter}>
                  <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="earned">Earned</SelectItem>
                    <SelectItem value="redeemed">Redeemed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={txnOffice} onValueChange={setTxnOffice}>
                  <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Offices</SelectItem>
                    {offices.map(o => (
                      <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={() => fetchTransactions(txnPage)}>
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {txnLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
            ) : transactions.length === 0 ? (
              <p className="text-center text-sm text-gray-400 py-8">No transactions yet</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-gray-500 uppercase tracking-wider">
                        <th className="pb-2 pr-4">Date</th>
                        <th className="pb-2 pr-4">Employee</th>
                        <th className="pb-2 pr-4">Signal</th>
                        <th className="pb-2 pr-4">Description</th>
                        <th className="pb-2 text-right">Coins</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn, i) => (
                        <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2.5 pr-4 text-xs text-gray-500">{txn.created_at?.slice(0, 10)}</td>
                          <td className="py-2.5 pr-4 text-sm font-medium">{txn.employee_name || 'Unknown'}</td>
                          <td className="py-2.5 pr-4">
                            <Badge variant="outline" className="text-[10px] font-mono">{txn.signal?.replace(/_/g, ' ')}</Badge>
                          </td>
                          <td className="py-2.5 pr-4 text-xs text-gray-600 max-w-[200px] truncate">{txn.description}</td>
                          <td className={`py-2.5 text-right font-bold text-sm ${txn.coins > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {txn.coins > 0 ? '+' : ''}{txn.coins}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                <div className="flex items-center justify-between mt-4">
                  <p className="text-xs text-gray-500">{txnTotal} transactions</p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={txnPage <= 1} onClick={() => fetchTransactions(txnPage - 1)}>
                      <ArrowLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-xs text-gray-500">Page {txnPage} of {txnTotalPages}</span>
                    <Button variant="outline" size="sm" disabled={txnPage >= txnTotalPages} onClick={() => fetchTransactions(txnPage + 1)}>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Balances Tab */}
      {activeTab === 'balances' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Select value={balOffice} onValueChange={setBalOffice}>
                <SelectTrigger className="w-48 h-8 text-xs"><SelectValue placeholder="All Offices" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Offices</SelectItem>
                  {offices.map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="sm" onClick={fetchBalances}>
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-500">Total: <strong className="text-amber-600">{balancesData.total_coins_in_circulation?.toLocaleString()} coins</strong></span>
              <span className="text-gray-500">Employees: <strong>{balancesData.total_employees}</strong></span>
            </div>
          </div>

          {balLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : balancesData.offices.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-sm text-gray-400">No coin balances yet</CardContent></Card>
          ) : (
            balancesData.offices.map(office => (
              <Card key={office.office_id}>
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedOffice(expandedOffice === office.office_id ? null : office.office_id)}
                >
                  <div className="flex items-center gap-3">
                    <Users2 className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{office.office_name}</p>
                      <p className="text-xs text-gray-400">{office.employees?.length || 0} employees</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-lg font-bold text-amber-600">{office.total_balance?.toLocaleString()}</p>
                      <p className="text-[10px] text-gray-400 uppercase">total coins</p>
                    </div>
                    {expandedOffice === office.office_id ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                  </div>
                </div>
                {expandedOffice === office.office_id && (
                  <CardContent className="pt-0 pb-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-gray-500 uppercase tracking-wider">
                          <th className="pb-2 pr-4">Employee</th>
                          <th className="pb-2 text-right">Earned</th>
                          <th className="pb-2 text-right">Redeemed</th>
                          <th className="pb-2 text-right">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {office.employees?.map((emp, i) => (
                          <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="py-2 pr-4 font-medium">{emp.name}</td>
                            <td className="py-2 text-right text-green-600">+{emp.total_earned?.toLocaleString()}</td>
                            <td className="py-2 text-right text-red-500">-{emp.total_redeemed?.toLocaleString()}</td>
                            <td className="py-2 text-right font-bold text-amber-600">{emp.balance?.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                )}
              </Card>
            ))
          )}
        </div>
      )}

      {/* Award Modal */}
      <Dialog open={showAwardModal} onOpenChange={setShowAwardModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-amber-500" /> Award Coins
            </DialogTitle>
            <DialogDescription>Manually award EcoCoins to an employee</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Employee</Label>
              <Select value={awardData.user_id} onValueChange={(v) => setAwardData({ ...awardData, user_id: v })}>
                <SelectTrigger data-testid="award-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.user_id || emp.id} value={emp.user_id || emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Coins to Award</Label>
              <Input data-testid="award-coins-input" type="number" placeholder="e.g. 50" value={awardData.coins}
                onChange={(e) => setAwardData({ ...awardData, coins: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Textarea data-testid="award-reason-input" placeholder="Why are you awarding these coins?" rows={2} value={awardData.reason}
                onChange={(e) => setAwardData({ ...awardData, reason: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAwardModal(false)}>Cancel</Button>
            <Button data-testid="award-confirm-btn" className="bg-amber-500 hover:bg-amber-600" disabled={awardProcessing} onClick={handleAward}>
              {awardProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Award className="h-4 w-4 mr-2" />}
              Award
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default EcoCoins;
